import { useMutation } from '@tanstack/react-query';
import {
  VendorContact,
  VendorContactGetAllParams,
  VendorCreateRequest,
  VendorCreateResponse,
  VendorCSVExportResponse,
  VendorGenericResponse,
  VendorGetAllParams,
  VendorPatchUpdateRequest,
  VendorResponse,
} from '@/types/vendor.types';
import axios, { AxiosError } from 'axios';
import axiosInstance from './axiosInstance';
import { request } from '@/services/request';
import { useQuery } from '@tanstack/react-query';
import { ListPageResponse } from '@/types/types';
import type { GenericErrorResponse, GenericErrorResponseError } from '@/types/api.types';

export const useUpdateVendorMutation = () => {
  return useMutation<
    VendorGenericResponse,
    AxiosError<GenericErrorResponse>,
    { vendorId: string; vendorData: VendorPatchUpdateRequest }
  >({
    mutationFn: async ({ vendorId, vendorData }) => {
      const response = await axios.patch<VendorGenericResponse>(`/vendors/${vendorId}`, {
        ...vendorData,
      });
      return response.data;
    },
  });
};

export const getVendors = async (query?: VendorGetAllParams) => {
  const response = await axios.get<ListPageResponse<'vendors', VendorResponse>>('/vendors', {
    params: query,
  });
  return response;
};

export const getVendorContactsByVendorId = async (vendorId?: string | null, query?: VendorContactGetAllParams) => {
  if (!vendorId) throw new Error('Vendor ID is required');

  const response = await axios.get<VendorContact[]>(`/vendors/${vendorId}/contacts`, { params: query });
  return response;
};

export const useGetVendorById = (vendorId: string | undefined) => {
  return useQuery<VendorResponse, GenericErrorResponseError>({
    queryKey: ['vendor', vendorId],
    queryFn: async () => {
      if (!vendorId) throw new Error('Vendor ID is required');
      const response = await axios.get<VendorResponse>(`/vendors/${vendorId}`);
      return response.data;
    },
    enabled: !!vendorId,
  });
};

export const useCreateVendorMutation = () => {
  return useMutation<VendorCreateResponse, GenericErrorResponse, VendorCreateRequest>({
    mutationFn: async (vendorData: VendorCreateRequest) => {
      const response = await axios.post<VendorCreateResponse>('/vendors', {
        ...vendorData,
      });
      return response.data;
    },
  });
};

export const useExportVendorsCsv = () =>
  useMutation<VendorCSVExportResponse, GenericErrorResponse, void>({
    // The server builds the whole list; the SDK this replaced had no timeout, so don't apply the 15s default.
    mutationFn: () =>
      request(() => axiosInstance.post<VendorCSVExportResponse>('/vendors/csv', undefined, { timeout: 5 * 60 * 1000 })),
  });
